import app from 'flarum/forum/app';

/** How long the bar's fetched stories stay usable before a refetch. */
const BAR_MAX_AGE = 60 * 1000;

/**
 * Shared knowledge of who currently has a story, and whose stories the actor
 * has already watched.
 *
 * Avatars render all over the forum, far from the story bar, so this lives
 * outside any component. The data ships with the page in the forum payload
 * rather than being fetched — an HTTP lookup used to land mid-render, and
 * Mithril drops redraws requested then, which is why rings appeared only after
 * some later unrelated redraw.
 */
class StoryStore {
  constructor() {
    this.authorIds = new Set();
    this.seenIds = new Set();
    this.followerOnlyIds = new Set();
    this.noBadgeIds = new Set();
    this.loaded = false;

    // The story bar's data, kept across IndexPage visits. See loadBar().
    this.bar = {
      groups: [],
      canCreate: false,
      loaded: false,
      error: false,
      request: null,
      fetchedAt: 0,
    };
  }

  /**
   * Read the author and seen sets out of the forum payload.
   *
   * Stays unloaded while `app.forum` is absent — it isn't populated yet while
   * initializers run — so the next call after boot seeds properly instead of
   * caching an empty result forever.
   */
  load() {
    if (this.loaded) return;
    if (!app.forum || typeof app.forum.attribute !== 'function') return;

    this.authorIds = toIdSet(app.forum.attribute('happydigitalStoryAuthorIds'));
    this.seenIds = toIdSet(app.forum.attribute('happydigitalStorySeenIds'));
    this.followerOnlyIds = toIdSet(app.forum.attribute('happydigitalStoryFollowerOnly'));
    this.noBadgeIds = toIdSet(app.forum.attribute('happydigitalStoryNoBadgeIds'));
    this.loaded = true;
  }

  /** Whether this user has an active story the actor may see. */
  hasStory(userId) {
    this.load();

    return this.authorIds.has(Number(userId));
  }

  /** Whether the actor has watched every active story from this user. */
  isSeen(userId) {
    this.load();

    return this.seenIds.has(Number(userId));
  }

  /**
   * Mark a user's ring as watched, so it dims without a page reload.
   *
   * These mutators seed first: otherwise a later lazy load would overwrite the
   * change with the payload the page arrived with, resurrecting a ring the
   * actor just deleted.
   */
  markSeen(userId) {
    this.load();
    this.seenIds.add(Number(userId));
  }

  /** Mark a user as having a story, after they post one. */
  markAuthor(userId) {
    this.load();
    this.authorIds.add(Number(userId));
    this.seenIds.delete(Number(userId));
  }

  /**
   * Whether a story badge may appear on this user's posts.
   *
   * Two gates: an admin switch that turns the feature off for the whole forum,
   * and the author's own preference within it.
   */
  showsBadge(userId) {
    this.load();

    if (!app.forum.attribute('happydigital-stories.showBadgeOnPosts')) return false;

    return !this.noBadgeIds.has(Number(userId));
  }

  /** Whether this user's active story is limited to their followers. */
  isFollowerOnly(userId) {
    this.load();

    return this.followerOnlyIds.has(Number(userId));
  }

  /** Drop a user's ring, after their last story goes away. */
  unmarkAuthor(userId) {
    this.load();
    this.authorIds.delete(Number(userId));
    this.seenIds.delete(Number(userId));
    this.followerOnlyIds.delete(Number(userId));
  }

  /**
   * The story bar's own data, held here rather than in the component.
   *
   * IndexPage rebuilds its hero every time it is entered, so the bar is a new
   * component with fresh state on every return from a discussion — it reloaded
   * and flashed its skeleton while the discussion list behind it, which Flarum
   * keeps on app.discussions, stayed put. Keeping the groups at module level
   * gives the bar the same persistence.
   *
   * @returns {Promise<void>} resolves once bar.groups is usable
   */
  loadBar(force) {
    // Stories expire on their own, with nothing to tell the page about it, so
    // held data goes stale by itself. A minute keeps a return from a discussion
    // instant while an expired story can't linger on the bar for long.
    const fresh =
      this.bar.loaded && Date.now() - this.bar.fetchedAt < BAR_MAX_AGE;

    if (!force && fresh) return Promise.resolve();

    // A second visit while the first request is still open waits for it
    // instead of firing another.
    if (this.bar.request) return this.bar.request;

    this.bar.error = false;

    this.bar.request = app
      .request({ method: 'GET', url: '/api/happydigital/stories' })
      .then((response) => {
        this.bar.groups = mergeSeen(
          this.bar.groups,
          (response && response.data) || []
        );
        this.bar.canCreate = !!(response && response.canCreate);
        this.bar.loaded = true;
        this.bar.fetchedAt = Date.now();
        this.bar.request = null;
      })
      .catch((e) => {
        this.bar.error = true;
        this.bar.request = null;

        throw e;
      });

    return this.bar.request;
  }

  /**
   * Fetch one user's stories, shaped like the bar's groups so StoryViewer can
   * consume them unchanged.
   *
   * @returns {Promise<Array>}
   */
  loadUser(userId) {
    return app
      .request({
        method: 'GET',
        url: '/api/happydigital/stories',
        params: { user: userId },
      })
      .then((response) => (response && response.data) || []);
  }
}

/**
 * Carry watched and liked marks from the held stories onto freshly fetched ones.
 *
 * A refetch can overtake the POST that records a view: the request leaves before
 * the server has the row, so the response says unwatched and replacing the list
 * wholesale threw away a mark the viewer had just earned. Marks only ever move
 * forward here — the server is trusted for everything else, and for a story the
 * page has not seen before it is trusted for these too.
 */
function mergeSeen(held, fetched) {
  if (!held.length) return fetched;

  const seen = new Map();

  held.forEach((group) => {
    group.stories.forEach((story) => {
      if (story.isViewed || story.isLiked) {
        seen.set(story.id, story);
      }
    });
  });

  if (!seen.size) return fetched;

  fetched.forEach((group) => {
    group.stories.forEach((story) => {
      const before = seen.get(story.id);

      if (!before) return;

      story.isViewed = story.isViewed || before.isViewed;
      story.isLiked = story.isLiked || before.isLiked;
    });

    // The ring follows from its stories, so recompute rather than trust the
    // flag that came with a response built before these marks existed.
    group.isViewed = group.stories.every((story) => story.isViewed);
  });

  return fetched;
}

function toIdSet(value) {
  return new Set((Array.isArray(value) ? value : []).map(Number));
}

export default new StoryStore();
